import React, { useState, useCallback } from 'react';
import { Select } from 'antd';
import { useDebounce } from '../../hooks/useDebounce';
import { userService, type Employee } from '../../services/userService';

interface UserSearchSelectProps {
  value?: string;
  onChange?: (email: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
  value,
  onChange,
  placeholder = "กรอกชื่อหรืออีเมล",
  style
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialValueLoaded, setInitialValueLoaded] = useState(false);
  
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Load initial value employee data if value exists
  React.useEffect(() => {
    const loadInitialValue = async () => {
      if (value && !initialValueLoaded) {
        try {
          const response = await userService.searchEmployees(value);
          if (response.employees && response.employees.length > 0) {
            setOptions(response.employees);
          }
          setInitialValueLoaded(true);
        } catch (error) {
          console.error('Error loading initial value:', error);
          setInitialValueLoaded(true);
        }
      }
    };
    
    loadInitialValue();
  }, [value, initialValueLoaded]);

  const searchEmployees = useCallback(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      // Don't clear options if we have an initial value loaded
      if (!initialValueLoaded || !value) {
        setOptions([]);
      }
      return;
    }

    setLoading(true);
    try {
      const response = await userService.searchEmployees(searchTerm);
      setOptions(response.employees);
    } catch (error) {
      console.error('Error searching employees:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [initialValueLoaded, value]);

  React.useEffect(() => {
    searchEmployees(debouncedSearchValue);
  }, [debouncedSearchValue, searchEmployees]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleChange = (selectedEmail: string) => {
    onChange?.(selectedEmail);
    setSearchValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // If user presses Enter and has typed something, validate and use it
    if (e.key === 'Enter' && searchValue && !options.some(opt => opt.email === searchValue)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(searchValue.trim())) {
        onChange?.(searchValue.trim());
        setSearchValue('');
        e.preventDefault();
      }
    }
  };

  const renderOption = (employee: Employee) => {
    const fullNameTh = `${employee.name_prefix_th}${employee.first_name_th} ${employee.last_name_th}`;
    
    return {
      label: (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
          <div style={{ fontWeight: 500 }}>
            {fullNameTh}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {employee.email}
          </div>
        </div>
      ),
      value: employee.email,
    };
  };

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      defaultActiveFirstOption={false}
      suffixIcon={null}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      onInputKeyDown={handleKeyDown}
      notFoundContent={
        loading 
          ? 'กำลังค้นหา...' 
          : searchValue.length < 2 
          ? 'กรุณาพิมพ์อย่างน้อย 2 ตัวอักษร' 
          : 'ไม่พบผลลัพธ์ (กด Enter เพื่อใช้อีเมลที่พิมพ์)'
      }
      options={options.map(renderOption)}
      loading={loading}
      style={{ width: '100%', ...style }}
      allowClear
    />
  );
};

export default UserSearchSelect;